import { Department, Employee, EmployeeRole } from '../../models/employee';
import { Project } from '../../models/project';
import { Task } from '../../models/task';
import { ApiResponse, httpService } from '../httpService';
import { AxiosResponse } from 'axios';

export const EmployeesApiUrl = '/api/Employees';

export interface GetEmployeesParams {
  skip?: number;
  take?: number;
  filter?: string;
  includeInactive?: boolean;
  includeDeleted?: boolean;
  role?: string;
  teamLeadGuid?: string;
  employeeType?: string;
  department?: string;
  managerId?: string;
  userRoleId?: string;
  roleId?: string | null;
}

export interface GetSalesPersonsParams {
  skip?: number;
  take?: number;
  filter?: string;
  includeInactive?: boolean;
  includeDeleted?: boolean;
}

export const getEmployees = async (
  params: GetEmployeesParams
): Promise<ApiResponse<Employee>> => {
  const response = await httpService.get<ApiResponse<Employee>>(
    EmployeesApiUrl,
    { params }
  );

  return response.data;
};

export const getEmployee = async (idEmployee: string): Promise<Employee> => {
  const response = await httpService.get<Employee>(
    `${EmployeesApiUrl}/${idEmployee}`
  );

  return response.data;
};

export const deleteEmployee = async (idEmployee: string): Promise<void> => {
  await httpService.delete(`${EmployeesApiUrl}/${idEmployee}`);
};

export interface GetEmployeeActivities {
  internalId: string;
  skip?: number;
  take?: number;
  startDate?: Date | string;
  endDate?: Date | string;
  filter?: string;
  includeDeleted?: boolean;
}

export interface GetProjectsByEmployee {
  internalId: string;
  managerId?: string;
  skip?: number;
  take?: number;
  filter?: string;
  includeDeleted?: boolean;
}

export interface GetDepartments {
  skip?: number | null;
  take?: number | null;
  filter?: string;
}

export const getEmployeeProjects = async (
  parameters: GetProjectsByEmployee
): Promise<ApiResponse<Project>> => {
  const response = await httpService.get<ApiResponse<Project>>(
    `${EmployeesApiUrl}/${parameters.internalId}/Projects`,
    {
      params: {
        skip: parameters.skip,
        take: parameters.take,
        filter: parameters.filter,
        includeDeleted: parameters.includeDeleted,
      },
    }
  );

  return response.data;
};

interface GetEmployeeTasksParams {
  internalId: string;
  projectId?: string;
  skip?: number;
  take?: number;
  filter?: string;
  includeDeleted?: boolean;
}

export const getEmployeeTasks = async (
  parameters: GetEmployeeTasksParams
): Promise<ApiResponse<Task>> => {
  const response = await httpService.get<ApiResponse<Task>>(
    `${EmployeesApiUrl}/${parameters.internalId}/Tasks`,
    {
      params: {
        projectId: parameters.projectId,
        skip: parameters.skip,
        take: parameters.take,
        filter: parameters.filter,
        includeDeleted: parameters.includeDeleted,
      },
    }
  );

  return response.data;
};

export const getUserPermissionGroups = async (): Promise<
  Array<EmployeeRole>
> => {
  const response = await httpService.get<Array<EmployeeRole>>(
    '/api/PermissionGroups'
  );

  return response.data;
};

export const getUserPermission = async (
  idEmployee: string
): Promise<Array<EmployeeRole>> => {
  const response = await httpService.get<Array<EmployeeRole>>(
    `/api/PermissionGroups/${idEmployee}`
  );

  return response.data;
};

export const addEmployeeRole = async (
  idEmployee: string,
  userPermissionGroupIds: string[]
): Promise<Employee> => {
  const result: AxiosResponse<Employee> = await httpService.put(
    `${EmployeesApiUrl}/${idEmployee}`,
    userPermissionGroupIds
  );

  return result.data;
};

export const getDepartments = async (
  params: GetDepartments
): Promise<ApiResponse<Department>> => {
  const response = await httpService.get<ApiResponse<Department>>(
    `${EmployeesApiUrl}/departments`,
    { params }
  );

  return response.data;
};

export const logLastLogin = async (): Promise<void> => {
  await httpService.put(`${EmployeesApiUrl}/login-time`);
};

interface UpdateEmployeeDetailsParams {
  id: string;
  workingHours: number;
}

export const updateEmployeeDetails = async (
  params: UpdateEmployeeDetailsParams
): Promise<void> => {
  await httpService.put(`${EmployeesApiUrl}/update-details`, params);
};
